import type { QueryClient } from 'react-query';

/**
 * Prefix shared by every billing query key. All of them are built through
 * `SettingsQueryKey(['billing', …])`, which flattens to
 * `['settings', 'billing', …]`, so react-query v3's partial matcher reaches the
 * whole set from this one key — including the parameterised variants
 * (`invoices` with `{ limit }`, `setupSummary`, `changePreview`) that an exact
 * key built through the `queryKey` helper would miss. No non-billing settings
 * key starts with it.
 */
export const BILLING_CACHE_PREFIX = ['settings', 'billing'];

/**
 * The cancellation funnel's status read is the one billing-adjacent query that
 * predates the `queryKey` helper and sits at the top level, outside the prefix.
 */
export const CANCELLATION_STATUS_KEY = 'cancellation-status';

/**
 * Invalidate every cache a billing mutation can possibly have changed.
 *
 * Deliberately blanket rather than per-mutation. Hand-listing keys inside each
 * `onSuccess` had failed six separate times across five test rounds — the
 * reporter filed A6 as "the fifth recorded occurrence" and A10 as "the third" —
 * because the lists drifted from what each endpoint actually touches:
 *
 *   - `billingEntitlements` was invalidated by NO mutation at all, yet it drives
 *     the add-on "included in your plan" badges and the module `source` chips.
 *     That is A6 (add-ons wrong after a trial→plan checkout) and Ac15 (GCM and
 *     Analytics reading active on Starter).
 *   - the trial→plan checkout (`useSetupSubscriptionMutation`) and the plan
 *     change (`useSubscribeMutation`) never invalidated `billingModules` or
 *     `billingUsage`, so module state and seat/profile meters stayed stale —
 *     Ac15 and Ac16.
 *   - `useActivateModuleMutation` invalidated only `billingModules`, though
 *     activation issues AND pays a prorated invoice, leaving the subscription,
 *     usage and invoice list stale.
 *   - `useUpdateAddonsMutation` charges the card immediately but never
 *     invalidated the invoices list.
 *   - `useReactivateMutation` never invalidated the cancellation-status read, so
 *     the "pending request" state survived a reactivate (CN1).
 *
 * The trade is deliberate and lopsided: billing is a settings page of ~10 small
 * reads, mutated rarely and only by explicit user action, so the cost of
 * over-invalidating is a handful of cheap GETs the user is already waiting on.
 * The cost of under-invalidating is a customer looking at wrong money. Scope it
 * per-mutation only if a measurement ever justifies it — and never at the price
 * of a mutation being able to forget a key again.
 */
export const invalidateBillingCaches = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries(BILLING_CACHE_PREFIX);
  queryClient.invalidateQueries(CANCELLATION_STATUS_KEY);
};
